import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  api, CONDITIONS, type CellStyle, type Column, type ConditionalRule,
} from '../lib/api';
import { R, btnAccent, btnOutline, chip, field, ft, label, mono } from '../lib/ui';
import { Modal } from './Modal';
import { Select } from './Select';
import { ColorButton } from './ToolButton';
import { useToast } from './Toast';

/** Правило человеческим языком — заголовок карточки. */
function describe(rule: ConditionalRule, columns: Column[]): string {
  const column = rule.colId
    ? columns.find((c) => c.fields.colId === rule.colId)?.fields.label ?? rule.colId
    : 'Любая колонка';

  const condition = CONDITIONS.find((c) => c.value === rule.op);
  if (!condition) return column;

  if (!condition.hasValue) return `${column} — ${condition.label.toLowerCase()}`;

  const value = rule.value ?? '';
  return condition.hasValue2
    ? `${column} ${condition.label.toLowerCase()} ${value} и ${rule.value2 ?? ''}`
    : `${column} ${condition.label.toLowerCase()} «${value}»`;
}

export function ConditionalDialog({
  docId, tableId, columns, onClose,
}: {
  docId: string;
  tableId: string;
  columns: Column[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const saved = useQuery({
    queryKey: ['conditional', docId, tableId],
    queryFn: () => api.conditionalRules(docId, tableId),
  });

  const [rules, setRules] = useState<ConditionalRule[]>([]);

  useEffect(() => {
    if (saved.data) setRules(saved.data);
  }, [saved.data]);

  const patch = useMutation({
    mutationFn: () => {
      // Удалить нужно те, что были сохранены, но из списка ушли.
      const remove = (saved.data ?? [])
        .filter((r) => r.id && !rules.some((x) => x.id === r.id))
        .map((r) => r.id!);

      return api.patchConditionalRules(docId, tableId, {
        upsert: rules.map((r, i) => ({ ...r, position: i })),
        remove,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conditional', docId, tableId] });
      toast('Правила сохранены', 'ok');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'err'),
  });

  const update = (index: number, patchRule: Partial<ConditionalRule>) =>
    setRules((list) => list.map((r, i) => (i === index ? { ...r, ...patchRule } : r)));

  const move = (index: number, delta: number) =>
    setRules((list) => {
      const target = index + delta;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((r, i) => ({ ...r, position: i }));
    });

  const add = () =>
    setRules((list) => [
      ...list,
      {
        colId: columns[0]?.fields.colId ?? null,
        op: 'gt',
        value: '',
        value2: null,
        style: { bg: '#fff4a3' },
        position: list.length,
      },
    ]);

  return (
    <Modal title="Условное форматирование" onClose={onClose} width={640}>
      <div style={{ fontSize: 12.5, color: R.textMuted, marginBottom: 14 }}>
        Правила применяются сверху вниз при просмотре. Первое совпавшее побеждает.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rules.map((rule, index) => {
          const condition = CONDITIONS.find((c) => c.value === rule.op);

          return (
            <div
              key={rule.id ?? index}
              style={{
                background: R.bg,
                border: `${index === 0 ? 2 : 1}px solid ${index === 0 ? R.accent : R.border}`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  borderBottom: ft.hair,
                }}
              >
                <span style={{ fontSize: 11, fontFamily: mono, color: R.textMuted }}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: R.text }}>
                  {describe(rule, columns)}
                </span>

                <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    title="Выше по приоритету"
                    style={{ ...chip(false), height: 22, opacity: index === 0 ? 0.4 : 1 }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(index, 1)}
                    disabled={index === rules.length - 1}
                    title="Ниже по приоритету"
                    style={{
                      ...chip(false),
                      height: 22,
                      opacity: index === rules.length - 1 ? 0.4 : 1,
                    }}
                  >
                    ↓
                  </button>
                </span>
              </div>

              <div style={{ padding: 12 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr auto',
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div>
                    <div style={label}>Колонка</div>
                    <Select
                      value={rule.colId ?? '__any__'}
                      onChange={(v) => update(index, { colId: v === '__any__' ? null : v })}
                      options={[
                        { value: '__any__', label: 'Любая колонка' },
                        ...columns.map((c) => ({
                          value: c.fields.colId,
                          label: c.fields.label,
                        })),
                      ]}
                    />
                  </div>

                  <div>
                    <div style={label}>Условие</div>
                    <Select
                      value={rule.op}
                      onChange={(op) => update(index, { op })}
                      options={CONDITIONS.map((c) => ({ value: c.value, label: c.label }))}
                    />
                  </div>

                  <button
                    onClick={() => setRules((list) => list.filter((_, i) => i !== index))}
                    title="Удалить правило"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 34,
                      height: 36,
                      alignSelf: 'end',
                      color: R.danger,
                      background: 'transparent',
                      border: ft.edge,
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {condition?.hasValue && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: condition.hasValue2 ? '1fr 1fr' : '1fr',
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <input
                      value={rule.value ?? ''}
                      placeholder="Значение"
                      onChange={(e) => update(index, { value: e.target.value })}
                      style={field}
                    />
                    {condition.hasValue2 && (
                      <input
                        value={rule.value2 ?? ''}
                        placeholder="…и"
                        onChange={(e) => update(index, { value2: e.target.value })}
                        style={field}
                      />
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 12, color: R.textMuted }}>Стиль:</div>

                  <ColorButton
                    icon={<span style={{ fontSize: 13, fontWeight: 700 }}>A</span>}
                    value={rule.style.fg}
                    title="Цвет текста"
                    onChange={(fg) => update(index, { style: { ...rule.style, fg } })}
                  />
                  <ColorButton
                    icon={<span style={{ fontSize: 13 }}>▧</span>}
                    value={rule.style.bg}
                    title="Цвет заливки"
                    onChange={(bg) => update(index, { style: { ...rule.style, bg } })}
                  />

                  <StyleToggle
                    checked={!!rule.style.b}
                    onChange={(on) => update(index, { style: { ...rule.style, b: on ? 1 : undefined } })}
                  >
                    <b>Ж</b>
                  </StyleToggle>

                  <StyleToggle
                    checked={!!rule.style.i}
                    onChange={(on) => update(index, { style: { ...rule.style, i: on ? 1 : undefined } })}
                  >
                    <i>К</i>
                  </StyleToggle>

                  <div
                    style={{
                      marginLeft: 'auto',
                      padding: '4px 10px',
                      background: rule.style.bg ?? '#fff',
                      color: rule.style.fg ?? '#000',
                      fontWeight: rule.style.b ? 600 : undefined,
                      fontStyle: rule.style.i ? 'italic' : undefined,
                      border: ft.edge,
                      fontSize: 12.5,
                    }}
                  >
                    Пример
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {rules.length === 0 && !saved.isLoading && (
          <div
            style={{
              padding: 16,
              textAlign: 'center',
              color: R.textMuted,
              fontSize: 13,
              border: `1px dashed ${R.border}`,
            }}
          >
            Правил пока нет — ячейки красятся только вручную.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={btnOutline} onClick={add}>
          + Правило
        </button>
        <span style={{ flex: 1 }} />
        <button style={btnOutline} onClick={onClose}>
          Отмена
        </button>
        <button style={btnAccent} disabled={patch.isPending} onClick={() => patch.mutate()}>
          {patch.isPending ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </Modal>
  );
}

function StyleToggle({
  checked, onChange, children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: R.textSecondary,
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: R.accent }}
      />
      {children}
    </label>
  );
}

export type { CellStyle };
